import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Navigation } from 'lucide-react';
import L from 'leaflet';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Normalize any coordinate format to [lat, lng] array
const toLatLng = (coords) => {
  if (!coords) return null;
  if (Array.isArray(coords) && coords.length >= 2) return [coords[0], coords[1]];
  if (typeof coords === 'object' && coords.lat != null && coords.lng != null) {
    return [Number(coords.lat), Number(coords.lng)];
  }
  return null;
};

// Build a Google Maps directions URL using coordinates when available
const getDirectionsUrl = (garage) => {
  const coordArr = toLatLng(garage.coordinates);
  if (coordArr) {
    return `https://www.google.com/maps/dir/?api=1&destination=${coordArr[0]},${coordArr[1]}`;
  }
  const addr = (garage.address || '').replace(/\s+/g, '+');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
};

// Create icon based on garage properties (custom style for fake, availability for live)
const createGarageIcon = (garage) => {
  const isFake = garage.isFake;
  
  // Custom locations use their own color/style/label
  if (isFake && garage.iconColor) {
    const color = garage.iconColor;
    const label = garage.iconLabel || 'P';
    const style = garage.iconStyle || 'circle';
    
    let html = '';
    const base = `background-color:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);`;
    
    switch (style) {
      case 'square':
        html = `<div style="${base}width:30px;height:30px;border-radius:4px;">${label}</div>`;
        return L.divIcon({ className: 'custom-parking-marker', html, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15] });
      case 'diamond':
        html = `<div style="${base}width:26px;height:26px;border-radius:4px;transform:rotate(45deg);"><span style="transform:rotate(-45deg);display:block;">${label}</span></div>`;
        return L.divIcon({ className: 'custom-parking-marker', html, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15] });
      case 'pin':
        html = `<div style="${base}width:28px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);"><span style="transform:rotate(45deg);display:block;font-size:11px;">${label}</span></div>`;
        return L.divIcon({ className: 'custom-parking-marker', html, iconSize: [28, 36], iconAnchor: [14, 36], popupAnchor: [0, -36] });
      case 'star':
        html = `<div style="width:34px;height:34px;background:${color};clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%);display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));"><span style="color:#fff;font-weight:bold;font-size:10px;margin-top:3px;">${label}</span></div>`;
        return L.divIcon({ className: 'custom-parking-marker', html, iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -17] });
      default: // circle
        html = `<div style="${base}width:30px;height:30px;border-radius:50%;">${label}</div>`;
        return L.divIcon({ className: 'custom-parking-marker', html, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -15] });
    }
  }
  
  // Live data locations: use availability-based coloring
  const color = garage.availabilityStatus === 'available' ? '#10b981' : 
               garage.availabilityStatus === 'limited' ? '#f59e0b' : '#ef4444';
  
  return L.divIcon({
    className: 'custom-parking-marker',
    html: `<div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 12px;
    ">P</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

// Component that controls map view to fit all markers
const MapController = ({ garages, mapCenter, mapZoom }) => {
  const map = useMap();

  useEffect(() => {
    const points = (garages || [])
      .map(g => toLatLng(g.coordinates))
      .filter(Boolean);
    
    if (points.length === 1) {
      map.setView(points[0], 15, { animate: true });
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16, animate: true });
    } else if (mapCenter) {
      const mc = toLatLng(mapCenter);
      if (mc) map.setView(mc, mapZoom || 14, { animate: true });
    }
  }, [map, garages, mapCenter, mapZoom]);
  
  return null;
};

const MapView = ({ garages, destinations, onDirectionsClick, onGarageClick, onFindLocation, locationStatus, userLocation, mapCenter, mapZoom, primaryColor, hoveredGarageId, onReserveClick, favorites, onToggleFavorite }) => {
  const brandColor = primaryColor || '#3b82f6';

  const createUserLocationIcon = () => {
    return L.divIcon({
      className: 'user-location-icon',
      html: `<div style="
        background-color: ${brandColor};
        border: 3px solid white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      "></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  };
  
  // Normalize all garage coordinates to [lat, lng] arrays
  const garagesWithCoords = useMemo(() => {
    return garages.map(garage => {
      const existing = toLatLng(garage.coordinates);
      if (existing) {
        return { ...garage, coordinates: existing };
      }
      
      // Fallback: known garage names
      let coords = null;
      switch (garage.name) {
        case 'Sapodilla Garage': coords = [26.7158, -80.0533]; break;
        case 'City Center': coords = [26.7145, -80.0528]; break;
        case 'Evernia': coords = [26.7163, -80.0541]; break;
        case 'Clematis Garage': coords = [26.7142, -80.0525]; break;
        case 'Banyan': coords = [26.7148, -80.0530]; break;
        default:
          if (garage.address) {
            if (garage.address.includes('Clematis')) coords = [26.7158, -80.0533];
            else if (garage.address.includes('Banyan')) coords = [26.7145, -80.0528];
            else if (garage.address.includes('Evernia')) coords = [26.7163, -80.0541];
          }
      }
      
      return { ...garage, coordinates: coords };
    });
  }, [garages]);

  // Only render garages that have valid coordinates
  const renderableGarages = useMemo(() => {
    return garagesWithCoords.filter(g => g.coordinates != null);
  }, [garagesWithCoords]);

  // Normalize destination coordinates
  const renderableDestinations = useMemo(() => {
    if (!destinations?.length) return [];
    return destinations.map(d => {
      const coords = toLatLng(d.coordinates);
      return coords ? { ...d, coordinates: coords } : null;
    }).filter(Boolean);
  }, [destinations]);

  // Create destination icon
  const createDestinationIcon = (dest) => {
    return L.divIcon({
      className: 'destination-marker',
      html: `<div style="
        background-color: #6366f1;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(99,102,241,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
      </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14],
    });
  };

  // Compute center and zoom dynamically based on data (include destinations)
  const { computedCenter, computedZoom, mapKey } = useMemo(() => {
    const garagePoints = renderableGarages.map(g => g.coordinates).filter(Boolean);
    const destPoints = renderableDestinations.map(d => d.coordinates).filter(Boolean);
    const points = [...garagePoints, ...destPoints];
    
    if (points.length === 1) {
      return { 
        computedCenter: points[0], 
        computedZoom: 15,
        mapKey: `map-${points[0][0].toFixed(4)}-${points[0][1].toFixed(4)}`
      };
    }
    if (points.length > 1) {
      // Compute center of all points
      const avgLat = points.reduce((s, p) => s + p[0], 0) / points.length;
      const avgLng = points.reduce((s, p) => s + p[1], 0) / points.length;
      return { 
        computedCenter: [avgLat, avgLng], 
        computedZoom: 13,
        mapKey: `map-multi-${points.length}-${avgLat.toFixed(4)}`
      };
    }
    // No garage coordinates — use manual override or neutral
    if (mapCenter) {
      const mc = toLatLng(mapCenter);
      if (mc) return { computedCenter: mc, computedZoom: mapZoom || 14, mapKey: `map-override` };
    }
    return { computedCenter: [39, -98], computedZoom: 4, mapKey: 'map-default' };
  }, [renderableGarages, renderableDestinations, mapCenter, mapZoom]);

  return (
    <div className="w-full h-[550px] rounded-lg overflow-hidden shadow-lg relative">
      {/* Find My Location Button */}
      <button
        onClick={onFindLocation}
        disabled={locationStatus === 'loading'}
        className="absolute top-4 right-4 z-[1000] flex items-center space-x-2 px-4 py-2 rounded-lg shadow-lg transition-all text-white"
        style={{
          backgroundColor: locationStatus === 'loading' ? '#9ca3af' : brandColor,
          cursor: locationStatus === 'loading' ? 'not-allowed' : 'pointer',
        }}
      >
        <Navigation className={`w-4 h-4 ${locationStatus === 'loading' ? 'animate-spin' : ''}`} />
        <span className="text-sm font-medium">
          {locationStatus === 'loading' ? 'Finding...' : 'Find My Location'}
        </span>
      </button>

      <MapContainer 
        key={mapKey}
        center={computedCenter} 
        zoom={computedZoom} 
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Dynamic view controller for multi-marker bounds fitting */}
        <MapController garages={renderableGarages} mapCenter={mapCenter} mapZoom={mapZoom} />
        
        {/* Garage Markers */}
        {renderableGarages.map((garage) => {
          const isHovered = hoveredGarageId === garage.id;
          const icon = createGarageIcon(garage);
          // For hovered markers, create a scaled-up version
          const highlightedIcon = isHovered ? L.divIcon({
            ...icon.options,
            className: (icon.options.className || '') + ' marker-highlighted',
            html: `<div style="transform:scale(1.5);filter:drop-shadow(0 0 8px rgba(59,130,246,0.6));transition:transform 0.2s ease;">${icon.options.html}</div>`,
          }) : icon;
          return (
            <Marker 
              key={garage.id} 
              position={garage.coordinates}
              icon={highlightedIcon}
              zIndexOffset={isHovered ? 1000 : 0}
            >
              <Popup maxWidth={340} minWidth={300}>
              <div>
                {/* Photo */}
                {garage.photoUrl && (
                  <img src={garage.photoUrl} alt={garage.name} style={{width:'100%',height:'120px',objectFit:'cover',borderRadius:'4px 4px 0 0',marginBottom:'0'}} />
                )}
                <div style={{padding:'12px'}}>
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="font-bold text-base text-slate-800">{garage.name}</h3>
                    <div className="flex items-center gap-1">
                      {onToggleFavorite && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(garage.id); }}
                          style={{padding:'2px',background:'none',border:'none',cursor:'pointer'}}
                          data-testid={`map-fav-btn-${garage.id}`}
                        >
                          <span style={{color: favorites?.includes(garage.id) ? '#ef4444' : '#cbd5e1',fontSize:'16px'}}>{favorites?.includes(garage.id) ? '❤' : '♡'}</span>
                        </button>
                      )}
                      {garage.locationType && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                          {garage.locationType === 'garage' ? 'Garage' : 'Surface'}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        garage.availabilityStatus === 'available' ? 'bg-green-100 text-green-700' :
                        garage.availabilityStatus === 'limited' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {garage.availabilityStatus === 'available' ? 'Available' : garage.availabilityStatus === 'limited' ? 'Limited' : 'Full'}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">{garage.address}</p>
                  {garage.distance != null && (
                    <p className="text-xs text-slate-400 mb-2" style={{display:'flex',alignItems:'center',gap:'4px'}}>
                      <span style={{fontSize:'12px'}}>📍</span> {garage.distance < 0.1 ? '< 0.1' : garage.distance} mi away
                    </p>
                  )}

                  {/* Stats */}
                  <div style={{background:'#f8fafc',borderRadius:'8px',padding:'10px',marginBottom:'10px'}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'6px',textAlign:'center',marginBottom:'6px'}}>
                      <div>
                        <div style={{fontSize:'18px',fontWeight:'bold',color:'#16a34a'}}>{garage.availableSpaces}</div>
                        <div style={{fontSize:'10px',color:'#64748b'}}>Available</div>
                      </div>
                      <div>
                        <div style={{fontSize:'18px',fontWeight:'bold',color:'#1e293b'}}>{garage.totalSpaces}</div>
                        <div style={{fontSize:'10px',color:'#64748b'}}>Total</div>
                      </div>
                      <div>
                        <div style={{fontSize:'18px',fontWeight:'bold',color: garage.utilizedPercent >= 90 ? '#dc2626' : garage.utilizedPercent >= 70 ? '#ca8a04' : '#16a34a'}}>{garage.utilizedPercent}%</div>
                        <div style={{fontSize:'10px',color:'#64748b'}}>Utilized</div>
                      </div>
                    </div>
                    <div style={{width:'100%',background:'#e2e8f0',borderRadius:'9999px',height:'8px'}}>
                      <div style={{height:'8px',borderRadius:'9999px',width:`${Math.max(garage.utilizedPercent||0,2)}%`,background: garage.utilizedPercent >= 90 ? '#ef4444' : garage.utilizedPercent >= 70 ? '#eab308' : '#22c55e'}} />
                    </div>
                  </div>

                  {/* Levels & Attributes Accordion */}
                  {((garage.levels && garage.levels.length > 0) || (garage.attributes && garage.attributes.length > 0)) && (
                    <details style={{marginBottom:'10px'}}>
                      <summary style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:'12px',color:'#475569',cursor:'pointer',padding:'6px 0',listStyle:'none',userSelect:'none'}}>
                        <span style={{fontWeight:'500'}}>
                          {garage.levels?.length ? `${garage.levels.length} levels` : ''}
                          {garage.levels?.length && garage.attributes?.length ? ' · ' : ''}
                          {garage.attributes?.length ? `${garage.attributes.length} types` : ''}
                        </span>
                        <span style={{fontSize:'10px',color:'#94a3b8'}}>tap to expand</span>
                      </summary>
                      <div style={{paddingTop:'6px'}}>
                        {garage.levels && garage.levels.length > 0 && (
                          <div style={{marginBottom:'8px'}}>
                            <div style={{fontSize:'10px',fontWeight:'600',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'4px'}}>By Level</div>
                            {garage.levels.map((level, i) => (
                              <div key={i} style={{marginBottom:'4px'}}>
                                <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',marginBottom:'2px'}}>
                                  <span style={{color:'#475569',fontWeight:'500'}}>{level.name}</span>
                                  <span style={{fontWeight:'700',color: level.utilizedPercent >= 90 ? '#dc2626' : level.utilizedPercent >= 70 ? '#ca8a04' : '#16a34a'}}>{level.availableSpaces}/{level.totalSpaces}</span>
                                </div>
                                <div style={{width:'100%',background:'#f1f5f9',borderRadius:'9999px',height:'4px'}}>
                                  <div style={{height:'4px',borderRadius:'9999px',width:`${Math.max(level.utilizedPercent,2)}%`,background: level.utilizedPercent >= 90 ? '#ef4444' : level.utilizedPercent >= 70 ? '#eab308' : '#22c55e'}} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {garage.attributes && garage.attributes.length > 0 && (
                          <div>
                            <div style={{fontSize:'10px',fontWeight:'600',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'4px'}}>By Type</div>
                            {garage.attributes.map((attr, i) => (
                              <div key={i} style={{marginBottom:'4px'}}>
                                <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',marginBottom:'2px'}}>
                                  <span style={{color:'#475569',fontWeight:'500'}}>{attr.name}</span>
                                  <span style={{fontWeight:'700',color: attr.utilizedPercent >= 90 ? '#dc2626' : attr.utilizedPercent >= 70 ? '#ca8a04' : '#16a34a'}}>{attr.availableSpaces}/{attr.totalSpaces}</span>
                                </div>
                                <div style={{width:'100%',background:'#f1f5f9',borderRadius:'9999px',height:'4px'}}>
                                  <div style={{height:'4px',borderRadius:'9999px',width:`${Math.max(attr.utilizedPercent,2)}%`,background: attr.utilizedPercent >= 90 ? '#ef4444' : attr.utilizedPercent >= 70 ? '#eab308' : '#22c55e'}} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </details>
                  )}

                  {/* Reserve button */}
                  {onReserveClick && (
                    <button
                      onClick={() => onReserveClick(garage)}
                      className="w-full px-3 py-2 rounded-lg text-sm font-semibold mb-2"
                      style={{ backgroundColor: 'transparent', border: `2px solid ${brandColor}`, color: brandColor, cursor: 'pointer' }}
                      data-testid={`map-reserve-btn-${garage.id}`}
                    >
                      Pay For Reserved Parking
                    </button>
                  )}
                  {/* Directions button */}
                  <button
                    onClick={() => window.open(getDirectionsUrl(garage), '_blank')}
                    className="w-full text-white px-3 py-2 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: brandColor }}
                  >
                    Get Directions
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}

        {/* User Location Marker */}
        {userLocation && (
          <Marker 
            position={[userLocation.lat, userLocation.lng]}
            icon={createUserLocationIcon()}
          >
            <Popup>
              <div className="p-2 text-center">
                <h3 className="font-bold" style={{ color: brandColor }}>Your Location</h3>
                <p className="text-sm text-slate-600">Current position</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Destination Markers */}
        {renderableDestinations.map((dest) => (
          <Marker
            key={`dest-${dest.id}`}
            position={dest.coordinates}
            icon={createDestinationIcon(dest)}
            zIndexOffset={500}
          >
            <Popup maxWidth={280} minWidth={220}>
              <div>
                {dest.imageUrl && (
                  <img src={dest.imageUrl} alt={dest.name} style={{width:'100%',height:'100px',objectFit:'cover',borderRadius:'4px 4px 0 0'}} />
                )}
                <div style={{padding:'10px'}}>
                  <h3 className="font-bold text-sm text-slate-800 mb-1">{dest.name}</h3>
                  <p className="text-xs text-slate-500 mb-2">{dest.address}</p>
                  {dest.url && (
                    <a
                      href={dest.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mb-2"
                      style={{display:'flex'}}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      Visit Website
                    </a>
                  )}
                  <span className="inline-flex items-center gap-1 text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                    Destination
                  </span>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg z-[1000]">
        <div className="text-xs font-medium text-slate-700 mb-2">Legend</div>
        <div className="flex flex-col space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full border border-white"></div>
            <span className="text-xs text-slate-600">Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
            <span className="text-xs text-slate-600">Limited</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full border border-white"></div>
            <span className="text-xs text-slate-600">Full</span>
          </div>
          {userLocation && (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full border border-white" style={{ backgroundColor: brandColor }}></div>
              <span className="text-xs text-slate-600">Your Location</span>
            </div>
          )}
          {renderableDestinations.length > 0 && (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-indigo-500 rounded-sm border border-white"></div>
              <span className="text-xs text-slate-600">Destination</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapView;
